import { useCallback } from 'react';

import {
  CloudArrowDownIcon,
  CurrencyDollarIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import { createColumnHelper } from '@tanstack/react-table';
import { sortBy } from 'lodash-es';
import { useDispatch } from 'react-redux';

import store from 'src/app/store';
import { defaultCurrencyFormat } from 'src/common/defaultCurrencyFormat';
import {
  ChargeStatusEnum,
  InvoiceRead,
  OverallPaymentStatusEnum,
} from 'src/common/external/bambi-api/bambiApi';
import { useLazyGetInvoicesExportListAsCsvQuery } from 'src/common/external/bambi-api/emptyApi';
import { Button } from 'src/common/primitives/Button';
import { hasRangeFilter } from 'src/common/primitives/NumberRangeFilterPopover/NumberRangeFilterPopover';
import { show } from 'src/common/primitives/Toast/toast.slice';
import { SimpleTooltip } from 'src/common/primitives/Tooltip';
import { Tag } from 'src/common/Tag/Tag';
import formatServerError from 'src/common/util/serverErrorFormatter';
import { PayersColumnFilter } from 'src/features/invoices/management/filters/PayersColumnFilter';

import {
  editInvoice,
  setInvoiceModalOpen,
  setPayModalOpen,
} from '../invoice.slice';
import { useInvoiceTotal } from '../useInvoiceTotal';
import { DateIssuedColumnFilter } from './filters/DateIssuedColumnFilter';
import { InvoiceTotalColumnFilter } from './filters/InvoiceTotalColumnFilter';
import { InvoiceTotalDueColumnFilter } from './filters/InvoiceTotalDueColumnFilter';
import { PaymentStatusColumnFilter } from './filters/PaymentStatusColumnFilter';

function ManageCell({ invoice }: { invoice: InvoiceRead }) {
  const dispatch = useDispatch();
  const { invoiceTotalDue } = useInvoiceTotal(invoice);

  const handleEditInvoice = useCallback(() => {
    dispatch(editInvoice(invoice));
    dispatch(setInvoiceModalOpen(true));
  }, [dispatch, invoice]);

  const handleShowPay = useCallback(() => {
    dispatch(editInvoice(invoice));
    dispatch(setPayModalOpen(true));
  }, [dispatch, invoice]);

  const [trigger] = useLazyGetInvoicesExportListAsCsvQuery();
  const handleExportInvoice = useCallback(async () => {
    try {
      await trigger({
        invoice: invoice.id,
        format: 'csv',
      }).unwrap();
    } catch (error) {
      dispatch(
        show({
          type: 'error',
          title: 'There was an error downloading the data.',
          description: formatServerError(error),
        })
      );
    }
  }, [dispatch, invoice, trigger]);

  return (
    <div className="flex flex-row items-center justify-end">
      <Button
        dataTestId={`edit-invoice-${invoice.id}`}
        variant="icon"
        onClick={() => handleEditInvoice()}
      >
        <PencilIcon className="h-[20px] w-[24px]" />
      </Button>
      <Button
        dataTestId={`pay-invoice-${invoice.id}`}
        variant="icon"
        onClick={() => handleShowPay()}
        disabled={invoiceTotalDue === 0}
      >
        <CurrencyDollarIcon className="w-[24px]" />
      </Button>
      <Button
        dataTestId={`export-invoice-${invoice.id}`}
        variant="icon"
        onClick={handleExportInvoice}
      >
        <CloudArrowDownIcon className="w-[24px]" />
      </Button>
    </div>
  );
}

const TagColumnWrapper = ({ children }: { children: React.ReactNode }) => {
  return <div style={{ width: 'fit-content' }}>{children}</div>;
};

const columnHelper = createColumnHelper<InvoiceRead>();

type ColorLabel = {
  color: string;
  label: string;
};

const paymentStatusToColorLabel: Record<OverallPaymentStatusEnum, ColorLabel> =
  {
    overpaid: {
      color: '#FF6C5C',
      label: 'Overpaid',
    },
    paid: {
      color: '#B6E2C1',
      label: 'Paid',
    },
    partially_paid: {
      color: '#F8F5C4',
      label: 'Partially paid',
    },
    unknown: {
      color: '#7C7977',
      label: 'Unknown',
    },
    unpaid: {
      color: '#F8D3D0',
      label: 'Unpaid',
    },
  };

const chargeStatusToColorLabel: Record<ChargeStatusEnum, ColorLabel> = {
  pending: {
    color: '#C1D8EE',
    label: 'Processing',
  },
  succeeded: {
    color: '#6AA546',
    label: 'Successful',
  },
  failed: {
    color: '#CC4123',
    label: 'Failed',
  },
};

export const columns = [
  columnHelper.accessor('number', {
    header: 'Number',
    enableSorting: true,
    enableColumnFilter: false,
  }),
  columnHelper.accessor('date_issued', {
    header: 'Date Issued',
    enableSorting: true,
    enableColumnFilter: true,
    meta: {
      filterComponent(column, open, onClose) {
        return <DateIssuedColumnFilter open={open} onClose={onClose} />;
      },
      filterCount() {
        const state = store.getState().invoice;
        const hasFilter = state.filterDateStart || state.filterDateEnd;

        return hasFilter ? 1 : 0;
      },
    },
  }),

  columnHelper.accessor('overall_payment_status', {
    header: 'Payment Status',
    cell({ getValue, row }) {
      const status = getValue();
      const anyPaymentsProcessing = row.original.payments.find(
        (p) => p.charge_status === 'pending'
      );

      const result = paymentStatusToColorLabel[status ?? 'unknown'];

      let tagContent: JSX.Element | null = <Tag {...result} />;

      if (anyPaymentsProcessing) {
        tagContent = <Tag color="#c1d8ee" label="Processing" />;
      } else if (!status || status === 'unknown') {
        tagContent = null;
      }

      return <TagColumnWrapper>{tagContent}</TagColumnWrapper>;
    },
    enableColumnFilter: true,
    meta: {
      filterComponent(column, open, onClose) {
        return <PaymentStatusColumnFilter open={open} onClose={onClose} />;
      },
      filterCount() {
        return store.getState().invoice.filterPaymentStatus?.length ?? 0;
      },
    },
  }),
  columnHelper.display({
    id: 'lastChargeStatus',
    header: 'Last Charge Status',
    cell({ row }) {
      const sortedPayments = sortBy(
        row.original.payments,
        'created_at'
      ).reverse();

      const lastPayment = sortedPayments[0];

      if (
        !lastPayment ||
        !chargeStatusToColorLabel[lastPayment.charge_status]
      ) {
        return null;
      }

      const { color, label } =
        chargeStatusToColorLabel[lastPayment.charge_status];

      return (
        <SimpleTooltip
          content={
            lastPayment.charge_status === 'failed'
              ? lastPayment.failure_message
              : null
          }
          trigger={
            <div
              style={{ width: 'fit-content' }}
              data-testid="last-charge-status"
            >
              <Tag color={color} label={label} />
            </div>
          }
        />
      );
    },
  }),
  columnHelper.accessor('total_amount_cents', {
    header: 'Invoice Total',
    cell({ getValue }) {
      return defaultCurrencyFormat(getValue());
    },
    enableColumnFilter: true,
    meta: {
      filterComponent(column, open, onClose) {
        return <InvoiceTotalColumnFilter open={open} onClose={onClose} />;
      },
      filterCount() {
        const state = store.getState().invoice;
        const hasFilter = hasRangeFilter(state.filterTotalAmount);

        return hasFilter ? 1 : 0;
      },
    },
  }),
  columnHelper.accessor('total_amount_due_cents', {
    header: 'Invoice Total Due',
    cell({ getValue }) {
      return defaultCurrencyFormat(getValue());
    },
    enableColumnFilter: true,
    meta: {
      filterComponent(column, open, onClose) {
        return <InvoiceTotalDueColumnFilter open={open} onClose={onClose} />;
      },
      filterCount() {
        const state = store.getState().invoice;
        const hasFilter = hasRangeFilter(state.filterTotalAmountDue);

        return hasFilter ? 1 : 0;
      },
    },
  }),
  columnHelper.accessor('notes', {
    header: 'Notes',
    cell({ getValue }) {
      return <div className="truncate">{getValue()}</div>;
    },
  }),
  columnHelper.accessor('line_items', {
    header: 'Payer',
    enableColumnFilter: true,
    meta: {
      filterComponent(column, open, onClose) {
        return <PayersColumnFilter open={open} onClose={onClose} />;
      },
      filterCount() {
        return store.getState().invoice.filterTripPayers?.length ?? 0;
      },
    },
    cell({ getValue }) {
      return (
        <div className="truncate">{getPayersFromLineItems(getValue())}</div>
      );
    },
  }),
  columnHelper.display({
    id: 'manage',
    enableHiding: false,
    header() {
      return <div className="mr-2 flex flex-row">Manage</div>;
    },
    cell(props) {
      return <ManageCell invoice={props.row.original} />;
    },
  }),
];

const getPayersFromLineItems = (lineItems: InvoiceRead['line_items']) => {
  return Array.from(
    new Set(
      lineItems
        .filter((lineItem) => lineItem.trip_payer?.display_name)
        .map((lineItem) => lineItem.trip_payer!.display_name)
    )
  )
    .sort()
    .join(', ');
};
